<template>
    <div class="cr:relative cr:w-full" v-click-outside="() => isOpen = false">
        <div
            class="cr:flex cr:items-center cr:justify-between cr:px-4 cr:py-2 cr:border cr:border-slate-300 cr:rounded-md cr:cursor-pointer cr:bg-white"
            @click="toggleDropdown"
        >
            <div class="cr:flex cr:flex-wrap cr:gap-2">
        <span
            v-if="selectedOptions.length === 0"
            class="cr:text-gray-400"
        >
          {{ placeholder }}
        </span>
                <span
                    v-for="(option, index) in selectedOptions"
                    :key="index"
                    class="cr:px-2 cr:py-1 cr:text-xs cr:text-white cr:rounded cr:font-semibold"
                    :class="[backgroundClasses]"
                >
          {{ option.id }}
        </span>
            </div>
            <div class="cr:absolute cr:right-1 cr:top-[50%] cr:translate-y-[-50%] cr:pointer-events-none cr:bg-white cr:p-3 cr:group-hover:bg-slate-50">
                <svg class="cr:w-3" viewBox="0 0 16 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd"
                          d="M0.467894 0.406234C0.767576 0.156774 1.17398 0.0166356 1.59773 0.0166356C2.02148 0.0166356 2.42788 0.156774 2.72756 0.406234L7.99001 4.78808L13.2525 0.406234C13.3999 0.279142 13.5762 0.17777 13.7712 0.108032C13.9662 0.0382934 14.1759 0.00158557 14.388 5.02416e-05C14.6002 -0.00148509 14.8107 0.0321825 15.0071 0.099089C15.2035 0.165996 15.3819 0.264801 15.5319 0.38974C15.682 0.514679 15.8006 0.663249 15.881 0.826782C15.9613 0.990314 16.0018 1.16553 15.9999 1.34222C15.9981 1.5189 15.954 1.69351 15.8703 1.85586C15.7865 2.0182 15.6648 2.16503 15.5121 2.28778L9.11985 7.6104C8.82016 7.85986 8.41376 8 7.99001 8C7.56626 8 7.15986 7.85986 6.86017 7.6104L0.467894 2.28778C0.168302 2.03825 0 1.69985 0 1.34701C0 0.994165 0.168302 0.655768 0.467894 0.406234Z"
                          fill="#00A3FF"/>
                </svg>
            </div>
        </div>

        <ul
            v-if="isOpen"
            class="cr:absolute cr:left-0 cr:right-0 cr:mt-2 cr:bg-white cr:border cr:border-slate-300 cr:rounded-md cr:shadow-2xl cr:max-h-48 cr:overflow-y-auto cr:z-[60] cr:bg-blue-500"
        >
            <li
                v-for="option in options"
                :key="option.id"
                class="cr:flex cr:items-center cr:px-4 cr:py-2 cr:hover:text-white cr:hover:font-semibold"
                :class="[hoverClasses]"
            >
                <label class="cr:flex cr:items-center cr:w-full cr:cursor-pointer">
                    <input
                        type="checkbox"
                        :value="option.id"
                        v-model="selectedValues"
                        class="cr:mr-2"
                    />
                    <span>{{ option.name }}</span>
                </label>
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
import {computed, ref, watch} from "vue";
import type MultiSelectOption from "@/interfaces/MultiSelectOption.ts";
import {useRegistrationStore} from "@/stores/registration.ts";
import {ORIGINS} from "@/services/ConstantSerice.ts";

const registrationStore = useRegistrationStore();
const props = defineProps({
    options: {
        type: Array as () => MultiSelectOption[],
        default: [],
    },
    modelValue: {
        type: Array,
        default: [],
    },
    placeholder: {
        type: String,
        default: 'Select options'
    }
});
const emit = defineEmits(['update:modelValue']);
const isOpen = ref(false);
const selectedValues = ref([...props.modelValue]);

const selectedOptions = computed(() =>
    props.options.filter((option) => selectedValues.value.includes(option.id))
);

watch(selectedValues, (newVal) => {
    emit("update:modelValue", newVal);
});

const toggleDropdown = () => {
    isOpen.value = !isOpen.value;
};

const hoverClasses = computed(() => {
    switch (registrationStore.registrationOrigin?.toLocaleLowerCase()) {
        case ORIGINS.BE:
            return 'cr:hover:bg-[var(--be-hover)]';
        case ORIGINS.KE:
            return 'cr:hover:bg-[var(--ke-hover)]';
        case ORIGINS.FIXR:
            return 'cr:hover:bg-[var(--fixr-hover)]';
        case ORIGINS.SR:
            return 'cr:hover:bg-[var(--sr-hover)]';
        case ORIGINS.RC:
            return 'cr:hover:bg-[var(--rc-hover)]';
        default:
            return 'cr:hover:bg-[var(--default-hover)]';
    }
});

const backgroundClasses = computed(() => {
    switch (registrationStore.registrationOrigin?.toLocaleLowerCase()) {
        case ORIGINS.BE:
            return 'cr:bg-[var(--be-primary)]';
        case ORIGINS.KE:
            return 'cr:bg-[var(--ke-primary)]';
        case ORIGINS.FIXR:
            return 'cr:bg-[var(--fixr-primary)]';
        case ORIGINS.SR:
            return 'cr:bg-[var(--sr-primary)]';
        case ORIGINS.RC:
            return 'cr:bg-[var(--rc-primary)]';
        default:
            return 'cr:bg-[var(--default-primary)]';
    }
});

</script>